import { Button, Col, Flex, Form, Input, InputNumber, List, Radio, Row, Select, Space, Tag, Tooltip, Typography, theme } from "antd";
import { useForm } from "antd/es/form/Form";
import React, { SetStateAction, useEffect, useMemo, useRef, useState } from "react"

import { EditOutlined } from "@ant-design/icons";
import { TelefoneService } from "../../Services/Contato/TelefoneService";
import { useQuery } from "@tanstack/react-query";
import { TypeTelefone } from "../../Models/Pessoa/Contato/Telefone";
import { InputTelefoneECelular } from "../InputTelefoneFixoECelular";

export const FormularioDeTelefones: React.FC<{
    telefonesAPI: [TypeTelefone[], React.Dispatch<SetStateAction<TypeTelefone[]>>]
    permitirEdicaoDosTelefones?: boolean
}> = (props) => {

    interface IDadosDoTelefonePlanificado extends TypeTelefone{
        codigoDoTipo?: number
    }

    const planificar = (formulario: TypeTelefone): IDadosDoTelefonePlanificado => {
        return {
            ...formulario,
            codigoDoTipo: formulario.tipo?.codigo
        }
    }

    const desplanificar = (formulario: IDadosDoTelefonePlanificado): TypeTelefone => {
        let objetoCorrigido: TypeTelefone = {
            ...formulario,
            tipo: {
                codigo: formulario.codigoDoTipo,
                tipo: tiposDeTelefoneDisponiveis.data?.data.find((t) => t.codigo === formulario.codigoDoTipo)?.tipo
            }
        }

        return objetoCorrigido;
    }

    const [IDDoTelefoneEmEdicao, setIDDoTelefoneEmEdicao] = useState<number | null>(null);
    const [IDDeAdicao, setIDDeAdicao] = useState<number>(-1)

    const [telefones, setTelefones] = props.telefonesAPI;

    const [form] = useForm<IDadosDoTelefonePlanificado>();

    const telefoneService = new TelefoneService();

    const tiposDeTelefoneDisponiveis = useQuery( {
        queryKey: [ "listarTipoTelefones" ],
        queryFn: () => telefoneService.listarTiposTelefones()
    })

    const estaEditandoUmTelefone = useMemo(() => {
        return IDDoTelefoneEmEdicao != null;
    }, [IDDoTelefoneEmEdicao, setIDDoTelefoneEmEdicao])

    const onFinish = (valores: IDadosDoTelefonePlanificado) => {
        valores.numero = valores.numero?.replaceAll('_', '').replaceAll('-', '')

        const objetoCorrigido = desplanificar(valores);

        objetoCorrigido.principal = telefones.find((t) => t.codigo == IDDoTelefoneEmEdicao)?.principal
        objetoCorrigido.ativo = true;
      
        if(estaEditandoUmTelefone) {
            objetoCorrigido.codigo = IDDoTelefoneEmEdicao!;
            setTelefones((prev) => prev.map((t) => t.codigo == IDDoTelefoneEmEdicao ? objetoCorrigido : t))
            setIDDoTelefoneEmEdicao(null);
        } else {
            objetoCorrigido.codigo = IDDeAdicao;
            setIDDeAdicao(IDDeAdicao - 1);
            setTelefones((prev) => [...prev, objetoCorrigido])
        }

        form.resetFields();
    }

    return <React.Fragment>

        <Flex vertical style={{width: '100%'}}>

            {IDDoTelefoneEmEdicao != null &&
                <Row gutter={16}>
                    <Col xs={24}>
                        {(() => {
                            const tel = telefones.find((v) => v.codigo == IDDoTelefoneEmEdicao)
                            if (tel == null) return;
                            return <Typography.Paragraph>
                                <Tag color='geekblue-inverse'> <EditOutlined /> {tel?.nomeContato}</Tag>
                            </Typography.Paragraph>
                        })()}
                    </Col>
                </Row>
            }

            <Form form={form} onFinish={onFinish} autoComplete="off">
                <Row gutter={16}>
                    <Col xs={24} md={4} xl={3}>
                        <Form.Item name="codigoDoTipo" rules={[{ required: true, message: 'Por favor, informe o tipo de telefone!' }]}>
                            <Select placeholder='Tipo'
                                id="telefone-codigo-tipo"
                                loading={tiposDeTelefoneDisponiveis.isLoading && tiposDeTelefoneDisponiveis.isFetching}
                                disabled={tiposDeTelefoneDisponiveis.isError}
                                options={tiposDeTelefoneDisponiveis.data?.data.map((item) => {
                                    return {
                                        value: item.codigo,
                                        label: item.tipo
                                    }
                                })}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={10} xl={5}>
                        <Form.Item name="nomeContato" rules={[{ required: true, message: 'Por favor, informe o nome do contato!' }]}>
                            <Input id="telefone-nome-contato" placeholder="Nome do Contato" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={10} xl={4}>
                        <Space.Compact>
                            <Form.Item style={{ width: "30%" }} name="ddd"
                                rules={[{ required: true, message: 'Por favor, informe o DDD!', pattern: /^\d{2}$/, }]}
                            >
                                <InputNumber id="telefone-ddd" controls={false} min={11} max={99} placeholder="DDD" />
                            </Form.Item>

                            <Form.Item style={{ width: "70%" }} name="numero"
                                rules={[
                                    { 
                                        required: true,
                                        message: 'Por favor, informe o número!'
                                    },
                                    {
                                        pattern: /^[0-9]{4,5}-[0-9]{4}$/,
                                        message: "Formato inválido! Use XXXXX-XXXX ou XXXX-XXXX.",
                                    }
                                ]}
                            >
                                <InputTelefoneECelular />
                            </Form.Item>
                        </Space.Compact>
                    </Col>

                    <Col xs={24} md={8} xl={6}>
                        <Form.Item name="ocupacao">
                            <Input id="telefone-ocupacao" placeholder="Ocupação" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={6} xl={2}>
                        <Form.Item name="ramal">
                            <Input id="telefone-ramal" placeholder="Ramal" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={4} xl={4} style={{ display: "flex", gap: "16px" }}>
                        {
                            <React.Fragment>
                                <Button type="primary" key="submit" htmlType='submit' style={{ flex: 1 }}>
                                    {estaEditandoUmTelefone ? "Salvar" : "Incluir telefone"}
                                </Button>

                                {estaEditandoUmTelefone &&
                                    <Button type="default" danger style={{ flex: 1 }}
                                        onClick={() => {
                                            setIDDoTelefoneEmEdicao(null);
                                            form.resetFields();
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                }
                            </React.Fragment>
                        }

                    </Col>
                </Row>

            </Form>

            <Row>
                <Col xs={24}>
                    <div style={{ paddingTop: 16 }}>
                        <List
                            header={<div className="common-list-header">Telefones adicionados</div>}
                            bordered
                            locale={{ emptyText: 'Nenhum telefone encontrado.' }}
                            dataSource={telefones.filter(f => f.ativo === true)}
                            renderItem={(item: TypeTelefone) =>
                                <List.Item
                                    className="common-list-item"
                                    actions={
                                        (() => {
                                            const excluir = (
                                                <Tooltip title={item.principal ? "Não é possível excluir o telefone principal." : null}>
                                                    <Button disabled={item.principal || estaEditandoUmTelefone} type='default' danger size='small' onClick={() => {
                                                        setTelefones((prev) => prev.map((t) => t.codigo == item.codigo ? { ...t, ativo: false } : t))
                                                    }}>
                                                        Excluir
                                                    </Button>
                                                </Tooltip> 
                                            )

                                            const editar = (
                                                <Button disabled={estaEditandoUmTelefone} type='primary' size='small'
                                                    onClick={() => {
                                                        if (item.codigo == null) return;

                                                        setIDDoTelefoneEmEdicao(item.codigo)
                                                        form.setFieldsValue(planificar(item))
                                                    }}>
                                                    Editar
                                                </Button> 
                                            )

                                            if(props.permitirEdicaoDosTelefones){
                                                return [editar, excluir]
                                            }

                                            return [excluir];
                                        })()
                                        
                                    }>

                                    <Row className="common-list-item-render">
                                        <Col>
                                            <span>
                                                <Radio checked={item.principal} onChange={() => {
                                                    setTelefones((prev) => prev.map((tel) => tel.codigo == item.codigo ? { ...tel, principal: true } : { ...tel, principal: false }))
                                                }} />
                                            </span>

                                            {item.principal &&
                                                <Tag color='green'>Principal</Tag>
                                            }

                                            <Tag color='blue'>{item.tipo?.tipo}</Tag>
                                            <Tag> {item.ddd} {item.numero?.replace('-', '')} </Tag>
                                            <Tag>  {item.nomeContato} </Tag>
                                        </Col>
                                    </Row>

                                </List.Item>
                            }
                        />
                    </div>
                </Col>
            </Row>

        </Flex>

    </React.Fragment>
}
